import { Feature } from 'geojson'
import { get, orderBy } from 'lodash-es'
import { DateTime } from 'luxon'
import { Dispatch, memo, SetStateAction, useMemo } from 'react'
import { LuCheck, LuTimer, LuTriangleAlert, LuX } from 'react-icons/lu'

import { HIGH_RISK_MARKER_COLOR } from '@/feature/observatory'
import { useSingleGeoFenceEvents } from '@/feature/observatory/api/geoFenceEvents'
import { GeoFence } from '@/feature/observatory/types/GeoFence.types'
import { GeoFenceEvent } from '@/feature/observatory/types/GeoFenceEvent.types'
import { getLongitudeLatitudeFromGeoFence } from '@/feature/map/utils/getLongitudeLatitudeFromGeoFence'
import { useTagsStore } from '@/feature/tags'

import { IconButton } from '@/lib/core/components/Button'
import { CircleAvatar } from '@/lib/core/components/CircleAvatar'
import { MarkerBadge } from '@/components/MarkerBadge'
import { Loader } from '@/lib/core/components/Loader'
import { ModalBase } from '@/lib/core/components/Modal'
import { Table, Td, Tr } from '@/lib/core/components/Table'
import { Tooltip } from '@/lib/core/components/Tooltip'
import { formatTimestamp } from '@/lib/core/constants/constants'

import styles from './GeofenceEventsModal.module.css'

export type GeofenceEventsModalProps = {
  geoFence?: GeoFence
  onClose: Dispatch<SetStateAction<boolean>>
}

export const DwellDuration = memo(function DwellDuration({
  enterTime,
  exitEvent,
  isExceededDwell,
  maxDwellTime,
}: {
  enterTime: string
  exitEvent: GeoFenceEvent | undefined
  isExceededDwell: boolean | undefined
  maxDwellTime?: number | null
}) {
  const enter = DateTime.fromISO(enterTime)
  const exitTime = exitEvent?.timestamp
  const exit = exitTime ? DateTime.fromISO(exitTime) : DateTime.now()
  const diffVal = exit.diff(enter)
  const diffTime = diffVal.toFormat("h'hrs' m'min'")

  if (
    isExceededDwell ||
    (isExceededDwell === undefined && maxDwellTime && diffVal.as('minutes') > maxDwellTime)
  ) {
    return (
      <div className={styles.dwellBlock}>
        <div className={styles.dwellDiffTime}>{diffTime}</div>
        <div>
          <Tooltip
            interactive
            trigger="click"
            arrowOffset={50}
            contentPadding={2}
            placement="left"
            tooltipContent="Dwell Time Exceeded"
          >
            <IconButton type="button" $variant="primary">
              <LuTimer size="16" color="#FF0000" strokeWidth={2.25} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    )
  }

  return diffTime
})

export const getExitTime = (event?: GeoFenceEvent): string => {
  if (event !== undefined) {
    if (event.expired === true) return 'Share Expired'

    return formatTimestamp(event.timestamp)
  } else {
    return ''
  }
}

export const isPolygon = (geoFence: GeoFence) => {
  return (geoFence.geoJson as Feature)?.geometry?.type === 'Polygon'
}

export const convertMinsToHrsMins = (mins: number) => {
  const hour = Math.floor(mins / 60)
  const m = mins % 60
  const min = m < 10 ? '0' + m : m
  return `${hour}hr ${min}min`
}

export const buildDetailsData = (
  geoFence: GeoFence | undefined,
  events: unknown,
  isLoading: boolean,
) => {
  if (geoFence === undefined) return [] as { label: string; value: React.ReactNode }[]
  const { lng, lat } = getLongitudeLatitudeFromGeoFence(geoFence)
  return [
    {
      label: 'Address',
      value: geoFence.address,
    },
    {
      label: 'Coordinates',
      value:
        Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))
          ? `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`
          : '-',
    },
    {
      label: 'Radius',
      value: isPolygon(geoFence) ? '-' : (geoFence.geoJson as Feature).properties?.radius + 'm',
    },
    {
      label: 'Currently Inside',
      value: isLoading ? <Loader size={'1rem'} /> : get(events, 'inside.length', 0),
    },
    {
      label: 'Max Dwell Time',
      value: geoFence.alertRules?.[0]?.dwellTime
        ? convertMinsToHrsMins(geoFence.alertRules[0].dwellTime)
        : '-',
    },
  ]
}

const columns = [
  {
    accessor: 'id',
    label: 'Reg Number',
  },
  {
    accessor: 'currentlyInside',
    label: 'Currently Inside',
  },
  {
    accessor: 'enter',
    label: 'Time In',
  },
  {
    accessor: 'exit',
    label: 'Time Out',
  },
  {
    accessor: 'dwellTime',
    label: 'Dwell Time',
  },
]

export const GeofenceEventsModal = ({
  onClose,
  geoFence,
}: Omit<GeofenceEventsModalProps, 'isActive'>) => {
  const { data, isLoading } = useSingleGeoFenceEvents(geoFence?.id)
  const events = geoFence ? get(data, geoFence?.id) : undefined

  const detailsData = useMemo(
    () => buildDetailsData(geoFence, events, isLoading),
    [geoFence, events, isLoading],
  )

  const { closeFilterPopout } = useTagsStore()

  const handleModalClose = () => {
    onClose(false)
    closeFilterPopout()
  }

  return (
    <ModalBase isActive={!!geoFence}>
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.title}>{geoFence?.title}</span>
          <button className={styles.closeButton} type="button" onClick={() => handleModalClose()}>
            <LuX size={24} color="#404040" strokeWidth={1.5} />
          </button>
        </div>
        <div className={styles.content}>
          <div className={styles.detailsContainer}>
            <div className={styles.detailsItemWrapper}>
              {geoFence?.highRiskArea && (
                <div className={styles.detailsIcon}>
                  <MarkerBadge glyph={LuTriangleAlert} color={HIGH_RISK_MARKER_COLOR} size={34} />
                </div>
              )}

              {detailsData.map((data) => (
                <div key={data.label} className={styles.detailsItem}>
                  <div className={styles.detailsItemLabel}>{data.label}</div>
                  <div className={styles.detailsItemContent}>{data.value}</div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.eventsContainer}>
            <Table columns={columns} isLoading={isLoading} header="Events Today">
              {orderBy(events?.records, ['exit', 'enter.timestamp'], ['desc', 'desc']).map(
                (event, id) => (
                  <Tr
                    key={`${event.vehicleBridgeId}${event.enter.timestamp}${id}`}
                    className={styles.tableRow}
                    $background={'none'}
                    $border={'1px solid #DDDDDD'}
                  >
                    <Td>
                      <div className={styles.tableRowPlateAndAvatar}>
                        {event.organization && (
                          <CircleAvatar
                            src={event.organization.avatar}
                            placeholder="empty"
                            size={32}
                          />
                        )}

                        {events?.assets?.[event.vehicleBridgeId.replaceAll('/', '_')]?.plateNumber}
                      </div>
                    </Td>
                    <Td>
                      <div className={styles.tableRowCurrentlyInside}>
                        {event.inside ? <LuCheck size={24} /> : null}
                      </div>
                    </Td>
                    <Td>{formatTimestamp(event.enter.timestamp)}</Td>
                    <Td>{getExitTime(event.exit)}</Td>
                    <Td>
                      <DwellDuration
                        enterTime={event.enter.timestamp}
                        exitEvent={event.exit}
                        isExceededDwell={event.isExceededDwell}
                        maxDwellTime={geoFence?.alertRules?.[0]?.dwellTime}
                      />
                    </Td>
                  </Tr>
                ),
              )}
            </Table>
          </div>
        </div>
      </div>
    </ModalBase>
  )
}
